/**
 * CSV building that survives commas.
 *
 * Written after a real bug: a hand-rolled export joined its header with
 * `join(',')` while quoting only the data cells. One header read
 * "Dialed, no connect", so the header line had 18 fields against the rows'
 * 17 and every column after it displayed under the wrong name. The data was
 * fine; the file was not.
 *
 * The lesson is that quoting cannot be applied selectively -- a value only
 * looks safe until someone writes a label with a comma in it, or a number
 * crosses 1,000 and picks up a thousands separator. So `csvRow` escapes
 * every field, unconditionally, header and body alike.
 *
 * Escaping follows RFC 4180: wrap in double quotes, and double any embedded
 * double quote.
 */

/** One field, escaped. Null and undefined become empty rather than the
 * strings "null" and "undefined", which is almost never what a spreadsheet
 * reader wants to see. */
export function csvField(value: unknown): string {
  if (value === null || value === undefined) return '""';
  const s = String(value);
  return `"${s.replace(/"/g, '""')}"`;
}

/** One line. Every field escaped, no exceptions. */
export function csvRow(values: unknown[]): string {
  return values.map(csvField).join(',');
}

/** A whole file: header row plus body rows, newline separated. */
export function buildCsv(header: unknown[], rows: unknown[][]): string {
  return [csvRow(header), ...rows.map(csvRow)].join('\n');
}

/** Trigger a browser download of `content` as `filename`. */
export function downloadCsv(filename: string, content: string): void {
  // BOM so Excel opens UTF-8 correctly rather than mangling accented
  // characters and the en-dashes these tables use for "not knowable".
  const blob = new Blob([`﻿${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
